/**
 * Read `output/manifest.json`'s `jobId` out of an HPC results zip IN THE BROWSER.
 *
 * The backend is the authority on whether a bundle may be imported into a
 * project (ProjectController.importFromHpc), but it can only check once the file
 * is in S3 — so a bundle picked for the wrong project was uploaded in full,
 * often hundreds of megabytes, before the rejection came back. Peeking the
 * manifest locally lets the UI refuse in milliseconds and skip the upload
 * entirely.
 *
 * This is an OPTIMIZATION, never a gate: every failure path returns null so the
 * import proceeds and the server decides. A bundle this can't parse (zip64, an
 * unusual compression method, a browser without DecompressionStream) must still
 * be importable.
 *
 * Only three small ranges of the file are ever read — the end-of-central-directory
 * record, the central directory, and the manifest entry itself — so peeking a
 * 400 MB bundle costs a few KB, not a full read.
 */

const EOCD_SIGNATURE = 0x06054b50;
const CENTRAL_HEADER_SIGNATURE = 0x02014b50;
const LOCAL_HEADER_SIGNATURE = 0x04034b50;

/** EOCD is 22 bytes plus a comment of at most 64 KB. */
const MAX_EOCD_SCAN = 22 + 0xffff;
/** A manifest is a few KB; refuse to pull anything absurd in its name. */
const MAX_MANIFEST_BYTES = 8 * 1024 * 1024;
/** 0xFFFFFFFF in an EOCD field means "see the zip64 record", which we don't parse. */
const ZIP64_SENTINEL = 0xffffffff;

interface CentralEntry {
  name: string;
  compressionMethod: number;
  compressedSize: number;
  localHeaderOffset: number;
}

/**
 * The project id these results were produced for, or null if it can't be read.
 * Null always means "let the server decide" — never "reject".
 */
export async function readHpcResultJobId(file: Blob): Promise<string | null> {
  try {
    const entries = await readCentralDirectory(file);
    if (!entries) return null;

    // Tolerate a wrapper directory, exactly as the backend and desktop importers do.
    const manifest = entries.find(
      (e) => e.name === 'output/manifest.json' || e.name.endsWith('/output/manifest.json'),
    );
    if (!manifest || manifest.compressedSize > MAX_MANIFEST_BYTES) return null;

    const bytes = await readEntryBytes(file, manifest);
    if (!bytes) return null;

    const jobId = JSON.parse(new TextDecoder().decode(bytes))?.jobId;
    return jobId == null || jobId === '' ? null : String(jobId);
  } catch {
    return null;
  }
}

async function readCentralDirectory(file: Blob): Promise<CentralEntry[] | null> {
  const size = file.size;
  if (size < 22) return null;

  const tailLength = Math.min(size, MAX_EOCD_SCAN);
  const tail = new DataView(await file.slice(size - tailLength).arrayBuffer());

  // Scan backwards for the record, and VALIDATE each candidate: the signature
  // can legally appear inside the zip comment, so "the last match" is not the
  // answer — a decoy sits AFTER the real record and would win. The real record
  // is the one whose declared comment length runs exactly to end-of-file.
  let eocd = -1;
  for (let i = tail.byteLength - 22; i >= 0; i--) {
    if (tail.getUint32(i, true) !== EOCD_SIGNATURE) continue;
    if (i + 22 + tail.getUint16(i + 20, true) === tail.byteLength) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) return null;

  const directorySize = tail.getUint32(eocd + 12, true);
  const directoryOffset = tail.getUint32(eocd + 16, true);
  if (directoryOffset === ZIP64_SENTINEL || directorySize === ZIP64_SENTINEL) return null;
  if (directoryOffset + directorySize > size) return null;

  const view = new DataView(
    await file.slice(directoryOffset, directoryOffset + directorySize).arrayBuffer(),
  );
  const decoder = new TextDecoder();
  const entries: CentralEntry[] = [];

  let p = 0;
  while (p + 46 <= view.byteLength) {
    if (view.getUint32(p, true) !== CENTRAL_HEADER_SIGNATURE) break;
    const nameLength = view.getUint16(p + 28, true);
    const extraLength = view.getUint16(p + 30, true);
    const commentLength = view.getUint16(p + 32, true);
    entries.push({
      name: decoder
        .decode(new Uint8Array(view.buffer, view.byteOffset + p + 46, nameLength))
        .replace(/\\/g, '/'),
      compressionMethod: view.getUint16(p + 10, true),
      compressedSize: view.getUint32(p + 20, true),
      localHeaderOffset: view.getUint32(p + 42, true),
    });
    p += 46 + nameLength + extraLength + commentLength;
  }
  return entries;
}

async function readEntryBytes(file: Blob, entry: CentralEntry): Promise<Uint8Array | null> {
  // The local header repeats the name/extra lengths, and its extra field may
  // differ in length from the central one — so the data offset must be computed
  // from the LOCAL header, not the central one.
  const header = new DataView(
    await file.slice(entry.localHeaderOffset, entry.localHeaderOffset + 30).arrayBuffer(),
  );
  if (header.byteLength < 30) return null;
  if (header.getUint32(0, true) !== LOCAL_HEADER_SIGNATURE) return null;

  const dataStart =
    entry.localHeaderOffset + 30 + header.getUint16(26, true) + header.getUint16(28, true);
  const raw = await file.slice(dataStart, dataStart + entry.compressedSize).arrayBuffer();

  if (entry.compressionMethod === 0) return new Uint8Array(raw); // stored
  if (entry.compressionMethod !== 8) return null; // not deflate — let the server read it
  return inflateRaw(raw);
}

async function inflateRaw(data: ArrayBuffer): Promise<Uint8Array | null> {
  // Not universal (Safari < 16.4, Firefox < 113) — without it the peek simply
  // doesn't happen and the server does the checking, as before.
  if (typeof DecompressionStream === 'undefined') return null;

  const ds = new DecompressionStream('deflate-raw');
  // Write and read concurrently: awaiting the write first would deadlock once a
  // payload exceeds the stream's buffer.
  const writer = ds.writable.getWriter();
  void writer.write(new Uint8Array(data)).then(() => writer.close());

  const reader = ds.readable.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      total += value.length;
    }
  }

  const out = new Uint8Array(total);
  let at = 0;
  for (const chunk of chunks) {
    out.set(chunk, at);
    at += chunk.length;
  }
  return out;
}
